// partners/adminView/pages/A7_AdminPayouts.jsx

import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  useGetAdminPayoutsQuery,
  useTriggerPayoutsMutation,
  useRetryFailedPayoutsMutation,
  usePreparePayoutsMutation,
} from "../../../apis/adminApiSlice";
import AdminLayout from "./A0_AdminLayout";
import { formatINR, formatUSD, formatUsdWithInr } from "../formatMoney";
import { capturePartnerEvent } from "../../../utils/analytics";

const STATUS_BADGE = {
  calculated: "bg-yellow-500/20 text-yellow-400",
  processing: "bg-mnf-cyan/20 text-mnf-cyan",
  paid:       "bg-mnf-success/20 text-mnf-success",
  failed:     "bg-mnf-error/20 text-mnf-error",
};

function ConfirmModal({
  open,
  title,
  lines,
  confirmText,
  tone = "cyan",
  onCancel,
  onConfirm,
}) {
  const toneClasses =
    tone === "danger"
      ? "from-mnf-error/80 to-rose-500/80 border-mnf-error/40"
      : "from-mnf-cyan/80 to-sky-500/80 border-mnf-cyan/40";

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-200 ${
        open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className={`absolute inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={onCancel}
      />
      <div
        className={`relative w-full max-w-lg rounded-mnf-lg border bg-mnf-dark-2 shadow-2xl overflow-hidden transition-all duration-200 ${
          open ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-95 opacity-0"
        }`}
      >
        <div className={`h-1.5 w-full bg-gradient-to-r ${toneClasses}`} />
        <div className="p-5">
          <h3 className="font-poppins text-lg font-semibold text-mnf-text-primary mb-3">
            {title}
          </h3>
          <div className="space-y-2 text-sm text-mnf-text-secondary leading-relaxed">
            {lines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-mnf-pill text-sm font-poppins font-semibold border border-white/20 text-mnf-text-secondary hover:border-white/40 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 rounded-mnf-pill text-sm font-poppins font-semibold text-mnf-btn-text bg-gradient-to-r ${toneClasses} transition-opacity`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminPayouts() {
  const now = new Date();
  const [month,    setMonth]    = useState(now.getMonth() + 1);
  const [year,     setYear]     = useState(now.getFullYear());
  const [feedback, setFeedback] = useState("");
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    lines: [],
    confirmText: "Confirm",
    tone: "cyan",
    onConfirm: () => {},
  });

  const { data, isLoading, refetch } = useGetAdminPayoutsQuery({ month, year });
  const [triggerPayouts,     { isLoading: triggering }] = useTriggerPayoutsMutation();
  const [retryFailedPayouts, { isLoading: retrying }]   = useRetryFailedPayoutsMutation();
  const [preparePayouts,     { isLoading: preparing }]  = usePreparePayoutsMutation();

  const payouts = data?.results || data || [];
  const summary = data?.summary;
  const viewedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && !viewedRef.current) {
      viewedRef.current = true;
      capturePartnerEvent("partner_admin_payouts_viewed");
    }
  }, [isLoading]);

  const closeConfirmModal = () => {
    setConfirmModal((prev) => ({ ...prev, open: false }));
  };

  const openConfirmModal = ({ title, lines, confirmText, tone = "cyan", onConfirm }) => {
    setConfirmModal({
      open: true,
      title,
      lines,
      confirmText,
      tone,
      onConfirm: async () => {
        closeConfirmModal();
        await onConfirm();
      },
    });
  };

  const runPrepare = async () => {
    try {
      const res = await preparePayouts({ month, year }).unwrap();
      setFeedback(res.message);
      capturePartnerEvent("partner_admin_payouts_prepared", { month, year, success: true });
      refetch();
    } catch (e) {
      capturePartnerEvent("partner_admin_payouts_prepared", { month, year, success: false });
      setFeedback(`Prepare failed: ${e?.data?.error || "Unknown error"}.`);
    }
  };

  const runTrigger = async () => {
    try {
      const res = await triggerPayouts({ month, year }).unwrap();
      const inrPart =
        res.total_amount_inr != null
          ? ` — ${formatINR(res.total_amount_inr)} transferred via RazorpayX`
          : "";
      setFeedback(
        `Paid: ${res.success} succeeded, ${res.failed} failed, ${res.skipped} skipped, ` +
          `${formatUSD(res.total_amount)} sent${inrPart}.`
      );
      capturePartnerEvent("partner_admin_payouts_triggered", {
        month,
        year,
        success: res.success,
        failed: res.failed,
        skipped: res.skipped,
      });
      refetch();
    } catch (e) {
      capturePartnerEvent("partner_admin_payouts_triggered", { month, year, success: 0, failed: 0, skipped: 0 });
      setFeedback(`Trigger failed: ${e?.data?.error || "Unknown error"}.`);
    }
  };

  const runRetry = async () => {
    try {
      const res = await retryFailedPayouts({ month, year }).unwrap();
      setFeedback(`Retry: ${res.success} succeeded, ${res.failed} still failing.`);
      capturePartnerEvent("partner_admin_payouts_retry_failed", {
        month,
        year,
        success: res.success,
        failed: res.failed,
      });
      refetch();
    } catch {
      capturePartnerEvent("partner_admin_payouts_retry_failed", { month, year });
      setFeedback("Retry failed.");
    }
  };

  const handlePrepare = () =>
    openConfirmModal({
      title: `Prepare payout records for ${month}/${year}?`,
      lines: [
        "Aggregates confirmed commissions per partner.",
        "Applies TDS (5% with PAN / 20% without, 0% if FY earnings <= ₹15,000).",
        "Creates payout rows ready for RazorpayX.",
      ],
      confirmText: "Prepare",
      tone: "cyan",
      onConfirm: runPrepare,
    });

  const handleTrigger = () =>
    openConfirmModal({
      title: `Send all calculated payouts for ${month}/${year}?`,
      lines: [
        "Stored payout totals are in USD; RazorpayX sends the INR equivalent (same FX as checkout).",
        "Ensure partners have bank/UPI details and fund accounts registered.",
      ],
      confirmText: "Send via RazorpayX",
      tone: "danger",
      onConfirm: runTrigger,
    });

  const handleRetry = () =>
    openConfirmModal({
      title: `Retry failed payouts for ${month}/${year}?`,
      lines: ["This will retry every payout currently marked as failed."],
      confirmText: "Retry failed",
      tone: "cyan",
      onConfirm: runRetry,
    });

  return (
    <AdminLayout>
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        lines={confirmModal.lines}
        confirmText={confirmModal.confirmText}
        tone={confirmModal.tone}
        onCancel={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
      />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
        <div>
          <h1 className="font-poppins text-2xl font-bold">Payouts</h1>
          <p className="text-sm text-mnf-text-muted mt-1 max-w-2xl">
            Pay partner commissions after they are calculated and confirmed on the{" "}
            <Link to="/partners/admin/commissions" className="text-mnf-cyan hover:underline">
              Commissions
            </Link>{" "}
            page.
          </p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
          >
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(2000, i).toLocaleString("en-IN", { month: "long" })}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            min="2024"
            max="2030"
            className="w-24 bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
          />
          <button
            onClick={handlePrepare}
            disabled={preparing}
            className="text-xs font-poppins font-semibold px-4 py-2 rounded-mnf-pill border border-mnf-cyan/30 text-mnf-cyan hover:bg-mnf-cyan/10 disabled:opacity-50 transition-colors"
          >
            {preparing ? "Preparing…" : "Prepare payouts"}
          </button>
          <button
            onClick={handleTrigger}
            disabled={triggering || (summary?.calculated === 0)}
            className="text-xs font-poppins font-semibold px-4 py-2 rounded-mnf-pill bg-mnf-gradient text-mnf-btn-text disabled:opacity-50 transition-opacity"
          >
            {triggering ? "Sending…" : "Send via RazorpayX"}
          </button>
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="text-xs font-poppins font-semibold px-4 py-2 rounded-mnf-pill border border-mnf-error/30 text-mnf-error hover:bg-mnf-error/10 disabled:opacity-50 transition-colors"
          >
            {retrying ? "Retrying…" : "Retry failed"}
          </button>
        </div>
      </div>

      <ol className="text-xs text-mnf-text-muted mb-6 list-decimal list-inside space-y-1 max-w-3xl">
        <li>On Commissions: calculate + confirm rows for this month.</li>
        <li>
          <strong className="text-mnf-text-secondary">Prepare payouts</strong> — builds payout rows with TDS.
        </li>
        <li>
          Ensure each partner has bank/UPI on their{" "}
          <Link to="/partners/admin/partners" className="text-mnf-cyan hover:underline">
            partner profile
          </Link>{" "}
          (Register fund account on RazorpayX).
        </li>
        <li>
          <strong className="text-mnf-text-secondary">Send via RazorpayX</strong> — transfers net amount;
          webhook marks paid + UTR.
        </li>
      </ol>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-4">
            <div className="text-xs text-mnf-text-muted uppercase mb-1">Pending (unpaid)</div>
            <div className="font-poppins text-lg font-bold text-yellow-400">
              {formatUSD(summary.pending_amount || 0)}
            </div>
          </div>
          <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-4">
            <div className="text-xs text-mnf-text-muted uppercase mb-1">Paid</div>
            <div className="font-poppins text-lg font-bold text-mnf-success">
              {formatUSD(summary.paid_amount || 0)}
            </div>
          </div>
          <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-4">
            <div className="text-xs text-mnf-text-muted uppercase mb-1">Ready to pay</div>
            <div className="font-poppins text-xl font-bold text-yellow-400">{summary.calculated}</div>
          </div>
          <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-4">
            <div className="text-xs text-mnf-text-muted uppercase mb-1">Paid rows</div>
            <div className="font-poppins text-xl font-bold text-mnf-success">{summary.paid}</div>
          </div>
          <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-4">
            <div className="text-xs text-mnf-text-muted uppercase mb-1">Gross</div>
            <div className="font-poppins text-lg font-bold">{formatUSD(summary.gross_total)}</div>
            {summary.gross_total_inr != null && (
              <div className="text-xs text-mnf-text-muted mt-0.5">
                {formatINR(summary.gross_total_inr)} at payout
              </div>
            )}
          </div>
          <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-4">
            <div className="text-xs text-mnf-text-muted uppercase mb-1">Net to send</div>
            <div className="font-poppins text-lg font-bold text-mnf-cyan">{formatUSD(summary.net_total)}</div>
            {summary.net_total_inr != null && (
              <div className="text-xs text-mnf-text-muted mt-0.5">
                {formatINR(summary.net_total_inr)} via RazorpayX
              </div>
            )}
          </div>
        </div>
      )}

      {feedback && <div className="mb-4 text-sm text-mnf-cyan">{feedback}</div>}

      {isLoading ? (
        <div className="text-mnf-text-muted text-sm">Loading...</div>
      ) : (
        <div className="bg-mnf-dark-2 border border-white/[0.08] rounded-mnf-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-mnf-dark-3 text-mnf-text-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3 text-left">Partner</th>
                <th className="px-5 py-3 text-left">Gross</th>
                <th className="px-5 py-3 text-left">TDS</th>
                <th className="px-5 py-3 text-left">Net</th>
                <th className="px-5 py-3 text-left">Method</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Paid at</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {payouts.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-8 text-center text-mnf-text-muted">
                    No payouts for {month}/{year}. Confirm commissions, then click Prepare payouts.
                  </td>
                </tr>
              )}
              {payouts.map((p) => (
                <tr key={p.id} className="border-t border-white/[0.04] hover:bg-mnf-dark-3 transition-colors">
                  <td className="px-5 py-3">
                    <div className="font-semibold">{p.partner_name || "—"}</div>
                    {p.partner_id && (
                      <Link
                        to={`/partners/admin/partners/${p.partner_id}`}
                        className="text-[11px] text-mnf-cyan hover:underline"
                      >
                        {p.partner_code}
                      </Link>
                    )}
                  </td>
                  <td className="px-5 py-3">{formatUsdWithInr(p.gross_amount, p.gross_amount_inr)}</td>
                  <td className="px-5 py-3 text-mnf-error">
                    {formatUSD(p.tds_amount)} ({p.tds_rate}%)
                  </td>
                  <td className="px-5 py-3 text-mnf-success font-semibold">
                    {formatUsdWithInr(p.net_amount, p.net_amount_inr)}
                  </td>
                  <td className="px-5 py-3 text-xs text-mnf-text-muted capitalize">{p.payment_method}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-xs font-poppins font-semibold px-2.5 py-1 rounded-full ${
                        STATUS_BADGE[p.status] || "bg-white/10 text-white"
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-mnf-text-muted">
                    {p.paid_at
                      ? new Date(p.paid_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
                      : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <Link to={`/partners/admin/payouts/${p.id}`} className="text-xs text-mnf-cyan hover:underline">
                      Detail →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
